import { zip } from 'fflate'

/**
 * Bundling ledger files into a zip, in the browser.
 *
 * Client-side rather than a server endpoint, because the files already come
 * through our own download proxy one at a time — so the browser can fetch the
 * same bytes and pack them, and no new streaming-zip route is needed. If a client
 * ever has hundreds of statements this should move server-side; for a handful of
 * cycles it is the smaller change by far.
 *
 * `fflate` rather than a hand-rolled writer: the stored-entry ZIP format is
 * simple enough to look tempting, and CRC32 plus offset arithmetic is exactly the
 * kind of code that passes a demo and then corrupts a 5 MB PDF or a filename with
 * an accent in it.
 */

/**
 * Pack named files into a zip Blob.
 *
 * Paths may contain slashes — `2026-08-20/statement.pdf` — and become real
 * folders in the archive, so the zip opens with the same dated structure the
 * screen shows.
 *
 * `level: 0` (store, no compression) on purpose: PDFs, images and Office files
 * are already compressed, so deflating them costs CPU and time to save almost
 * nothing. Text-ish files are small enough for it not to matter.
 *
 * @param {Array<{path: string, bytes: Uint8Array}>} files
 * @returns {Promise<Blob>}
 */
export function buildZip(files) {
  const tree = {}
  for (const f of files) {
    // Duplicate paths would silently overwrite each other, so a repeat gets a
    // numeric suffix — two files called `statement.pdf` in one cycle is unusual
    // but losing one of them without a word is worse.
    let path = f.path
    let n = 2
    while (tree[path]) {
      const dot = f.path.lastIndexOf('.')
      path = dot > 0 ? `${f.path.slice(0, dot)} (${n})${f.path.slice(dot)}` : `${f.path} (${n})`
      n += 1
    }
    tree[path] = [f.bytes, { level: 0 }]
  }

  return new Promise((resolve, reject) => {
    zip(tree, { level: 0 }, (err, data) => {
      if (err) reject(err)
      else resolve(new Blob([data], { type: 'application/zip' }))
    })
  })
}

/** Hand a Blob to the browser as a download. */
export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Revoked on the next tick — doing it synchronously can cancel the download
  // before the browser has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
